/**
 * Lecture des bornes de parcours dans le calendrier de l'organisation (#194).
 *
 * Ces dates sortent de l'API en instants déjà résolus : `startDate` au début du
 * jour local de l'org, `endDate` à sa fin (23:59:59.999). Les relire dans le
 * fuseau du NAVIGATEUR les décale — d'un jour entier pour une fin de journée
 * chez une org à l'ouest de Greenwich, où le 30 septembre 23:59 est déjà le
 * 1er octobre en UTC. D'où le `timeZone` sur chaque appel, et non en option.
 *
 * Trois écrans portaient chacun leur `formatDate`, à la virgule près. Trois
 * copies, c'est trois occasions d'oublier le fuseau à la prochaine correction.
 */

/** Fuseau de repli tant que la config d'org n'est pas chargée (miroir de `DEFAULT_TIMEZONE`). */
const FALLBACK_TIMEZONE = 'Europe/Paris'

function toDate(value: unknown): Date | null {
  if (!value) return null
  const date = new Date(value as string)
  return Number.isNaN(date.getTime()) ? null : date
}

function resolveLocale(language: string): string {
  return language === 'en' ? 'en-US' : 'fr-FR'
}

/**
 * Jour civil (`AAAA-MM-JJ`) porté par cet instant dans le fuseau de l'org, pour
 * réalimenter un `<input type="date">`.
 *
 * `en-CA` est le seul format qu'`Intl` rend directement en ISO ; le reconstruire
 * à partir des `parts` reviendrait au même en plus long.
 */
export function toDayInput(value: unknown, timeZone?: string | null): string {
  const date = toDate(value)
  if (!date) return ''
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: timeZone ?? FALLBACK_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date)
}

/** Format de `formatSessionDate` avec l'année, pour les dates qui peuvent tomber hors de l'année en cours. */
export const DATE_WITH_YEAR: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'long', day: 'numeric' }

/** Date avec l'heure, pour un horodatage (une connexion, #242). */
export const DATE_TIME_WITH_YEAR: Intl.DateTimeFormatOptions = { ...DATE_WITH_YEAR, hour: '2-digit', minute: '2-digit' }

/**
 * Date de parcours en toutes lettres, dans la langue de l'utilisateur et le
 * calendrier de l'organisation.
 *
 * Le format par défaut omet l'année : sur un écran qui liste des échéances
 * proches, elle n'apporte rien. Les appelants qui listent des parcours passés
 * la redemandent.
 */
export function formatSessionDate(
  value: unknown,
  language: string,
  timeZone?: string | null,
  options: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'long' },
): string {
  const date = toDate(value)
  if (!date) return ''

  const locale = resolveLocale(language)
  const text = date.toLocaleDateString(locale, {
    ...options,
    timeZone: timeZone ?? FALLBACK_TIMEZONE,
  })

  // « le 1 septembre » se lit comme une faute. `Intl` ne connaît pas l'ordinal
  // du premier du mois, seule irrégularité du français.
  return locale === 'fr-FR' ? text.replace(/^1 /, '1er ') : text
}

/** Heure d'un instant (« 09:05 »), dans la langue de l'utilisateur et le fuseau de l'organisation. */
export function formatTimeOfDay(value: unknown, language: string, timeZone?: string | null): string {
  const date = toDate(value)
  if (!date) return ''
  return date.toLocaleTimeString(resolveLocale(language), { hour: '2-digit', minute: '2-digit', timeZone: timeZone ?? FALLBACK_TIMEZONE })
}
